import React from 'react';

const ApplicationWorkflow = () => {
  const steps = [
    {
      id: 1,
      title: "Register or Login",
      description: "Existing Users: Login with your credentials\nGo directly to dashboard",
      newUserText: "New Users:\nCreate NOC account with basic details Instant access after registration",
      color: "bg-green-500",
      icon: "👤"
    },
    {
      id: 2,
      title: "Choose Your Tanks",
      description: "Browse water tanks across Tamil Nadu\nFilter by location and department\nSelect tanks for your project",
      color: "bg-yellow-500",
      icon: "🏗️"
    },
    {
      id: 3,
      title: "Plan Your Activities",
      description: "Choose restoration activities for each tank Enter project budget Upload required project files",
      color: "bg-blue-500",
      icon: "📋"
    },
    {
      id: 4,
      title: "Submit & Track",
      description: "Submit application to departments\nTrack progress",
      color: "bg-green-600",
      icon: "📤"
    }
  ];

  const departmentReviews = [
    { level: "L1", name: "Department Review", days: "7 Working Days", color: "bg-blue-500" },
    { level: "L2", name: "Department Review", days: "5 Working Days", color: "bg-red-500" },
    { level: "L3", name: "Department Review", days: "5 Working Days", color: "bg-yellow-500" },
    { level: "", name: "Collector Approval & NOC Issue", days: "5 Working Days", color: "bg-purple-500" }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap');
        
        .workflow-font {
          font-family: 'Poppins', 'Inter', system-ui, sans-serif;
        }
      `}</style>
      
      <section className="py-36 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 workflow-font">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent mb-3">
              Application Workflow
            </h2>
            <p className="text-gray-600 mb-4">
              Simple steps to get your water tank restoration project approved
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>

          {/* Main Workflow Steps */}
          <div className="relative mb-16">
            {/* Connection Line */}
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-200 transform -translate-y-1/2 z-0 hidden lg:block"></div>
            
            <div className="grid lg:grid-cols-4 gap-6 lg:gap-4 relative z-10">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center">
                  {/* Step Circle */}
                  <div className={`w-14 h-14 ${step.color} rounded-full flex items-center justify-center text-white text-xl font-bold mb-4 shadow-lg`}>
                    {step.id}
                  </div>
                  
                  {/* Step Content */}
                  <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100 text-center max-w-sm hover:shadow-xl transition-shadow duration-300">
                    <h3 className="font-semibold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                      Step {step.id}: {step.title}
                    </h3>
                    
                    {/* Main Description */}
                    <div className="text-sm text-gray-600 mb-3">
                      {step.description.split('\n').map((line, i) => (
                        <div key={i} className="mb-1">{line}</div>
                      ))}
                    </div>
                    
                    {/* New User Text for Step 1 */}
                    {step.newUserText && (
                      <div className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
                        {step.newUserText.split('\n').map((line, i) => (
                          <div key={i} className="mb-1">{line}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Department Review Process */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl lg:text-3xl font-bold text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
              Department Review Process
            </h3>
            <p className="text-center text-gray-600 mb-8">
              Your application goes through multiple review stages
            </p>
            
            <div className="relative">
              {/* Connection Line for Reviews */}
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-200 via-pink-200 to-red-200 transform -translate-y-1/2 z-0 hidden lg:block"></div>
              
              <div className="grid lg:grid-cols-4 gap-6 relative z-10">
                {departmentReviews.map((review, index) => (
                  <div key={index} className="flex flex-col items-center">
                    {/* Review Circle */}
                    <div className={`w-12 h-12 ${review.color} rounded-full flex items-center justify-center text-white text-sm font-bold mb-4 shadow-lg`}>
                      {review.level || "✓"}
                    </div>
                    
                    {/* Review Card */}
                    <div className="bg-white rounded-lg p-4 text-center shadow-md border border-gray-100 w-full max-w-xs hover:shadow-lg transition-shadow duration-300">
                      <h4 className="font-medium text-gray-800 text-sm mb-2">
                        {review.name}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {review.days}
                      </p>
                    </div>
                    
                    {/* Arrow (except for last item) */}
                    {index < departmentReviews.length - 1 && (
                      <div className="lg:hidden mt-4 mb-4">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m0 7l7-7" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Total Processing Time */}
            <div className="text-center mt-8 p-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg border border-blue-200">
              <p className="text-lg font-semibold bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                Total Processing Time: 22 Working Days
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ApplicationWorkflow;